import { useState, useEffect, useRef } from "react";
import { triggerCacheRefresh, getCacheRefreshStatus } from "../api/cache";
import { RefreshStatus } from "../shared/api-types/cache";

interface CacheRefreshConfig {
  baseUrl: string;
  cachePrefix: string;
}

interface UseCacheRefreshOptions {
  config: CacheRefreshConfig;
  crewId: number;
  onRefreshComplete?: () => void;
}

export const useCacheRefresh = ({ config, crewId, onRefreshComplete }: UseCacheRefreshOptions) => {
  const refreshConfig = {
    baseUrl: config.baseUrl,
    cachePrefix: config.cachePrefix,
    crewId,
  };
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState<number>(0);
  const [oldestCacheTime, setOldestCacheTime] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [dataUpdated, setDataUpdated] = useState(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const startPolling = () => {
    // Clear any existing poll
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(async () => {
      try {
        const status = await getCacheRefreshStatus(refreshConfig);

        if (status.status === RefreshStatus.COMPLETED) {
          // Refresh complete - stop polling
          stopPolling();
          setIsRefreshing(false);

          // Update timestamp immediately to avoid showing old time
          if (status.completedAt) {
            setOldestCacheTime(status.completedAt);
          }

          // Trigger success animation
          setDataUpdated(true);
          setTimeout(() => setDataUpdated(false), 1800);

          // Reload data with fresh cache
          setRefreshKey((prev) => prev + 1);

          // Call optional callback
          if (onRefreshComplete) {
            onRefreshComplete();
          }
        } else if (status.status === RefreshStatus.PROCESSING) {
          // Update progress
          setIsRefreshing(true);
          setRefreshProgress(status.percentage || 0);
        } else if (status.status === RefreshStatus.FAILED) {
          // Refresh failed
          stopPolling();
          setIsRefreshing(false);
          console.error("Cache refresh failed:", status.error);
        } else if (status.status === RefreshStatus.IDLE) {
          // No refresh in progress
          stopPolling();
          setIsRefreshing(false);
        }
      } catch (error) {
        console.error("Failed to check refresh status:", error);
      }
    }, 2000);
  };

  const forceRefresh = async () => {
    try {
      // Trigger background refresh
      const result = await triggerCacheRefresh(refreshConfig);

      if (result.status === RefreshStatus.ALREADY_RUNNING) {
        // Already refreshing, just start polling
        startPolling();
        return;
      }

      setIsRefreshing(true);
      setRefreshProgress(0);

      // Start polling for status
      startPolling();
    } catch (error) {
      console.error("Failed to trigger cache refresh:", error);
    }
  };

  // Check for in-progress refresh on mount
  useEffect(() => {
    if (!crewId) return;
    (async () => {
      const status = await getCacheRefreshStatus(refreshConfig);
      if (status.status === RefreshStatus.PROCESSING) {
        setIsRefreshing(true);
        setRefreshProgress(status.percentage || 0);
        startPolling();
      }
    })();

    // Cleanup polling on unmount
    return () => stopPolling();
  }, [crewId]);

  // Reset refreshKey when crew changes
  useEffect(() => {
    setRefreshKey(0);
  }, [crewId]);

  return {
    isRefreshing,
    refreshProgress,
    oldestCacheTime,
    refreshKey,
    dataUpdated,
    forceRefresh,
    setOldestCacheTime,
  };
};
